import { useEffect } from "react";
import { accentBg } from "../utils/accent";

/**
 * Termos de Uso — AutEM
 *
 * Página pública (não requer autenticação). Linkada a partir do login.
 * Documento em PT-BR alinhado à LGPD (Lei 13.709/2018) e ao Marco Civil da Internet.
 */
export default function TermsPage() {
  useEffect(() => {
    document.title = "Termos de Uso, AutEM";
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="terms-page">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/login" className="inline-flex items-center gap-2">
            <img src="/logo_light.png" alt="AutEM" className="h-8 object-contain" />
          </a>
          <a
            href="/login"
            className="text-sm font-semibold hover:underline"
            style={{ color: "var(--accent)" }}
            data-testid="terms-back-login"
          >
            ← Voltar ao login
          </a>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-6">
        <p className="text-xs uppercase tracking-widest font-semibold text-gray-500 mb-2">
          Documento legal
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Termos de Uso
        </h1>
        <p className="text-gray-500 text-sm">
          Última atualização: 01 de fevereiro de 2026 · Versão 1.0
        </p>
        <div className="mt-4 h-1 w-16 rounded-full" style={accentBg()} />
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 pb-24 prose prose-slate">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-10 shadow-sm text-[15px] leading-relaxed text-gray-700 space-y-8">

          <section>
            <p className="text-sm text-gray-500 border-l-4 pl-4" style={{ borderColor: "var(--accent)" }}>
              Bem-vindo(a) ao <strong className="text-gray-900">AutEM</strong>. Este documento reúne
              as condições que regem o acesso e uso da plataforma AutEM, um sistema SaaS de gestão
              inteligente de serviços de campo (Field Service Management) direcionado a operações de
              socorro automotivo e logística. Ao clicar em &ldquo;Entrar&rdquo; ou continuar utilizando a
              plataforma, você declara ter lido, compreendido e aceito integralmente estes Termos.
            </p>
          </section>

          <Section title="1. Definições">
            <p><strong>1.1.</strong> &ldquo;AutEM&rdquo; ou &ldquo;Plataforma&rdquo;: o software SaaS multi-tenant fornecido
              pela AutEM Tecnologia, incluindo painel web, aplicativos móveis e APIs.</p>
            <p><strong>1.2.</strong> &ldquo;Cliente&rdquo; ou &ldquo;Contratante&rdquo;: pessoa jurídica que contrata o
              serviço para uso próprio e de seus colaboradores.</p>
            <p><strong>1.3.</strong> &ldquo;Usuário&rdquo;: qualquer pessoa física autorizada pelo Cliente a
              acessar o AutEM (administrador, operador, profissional de campo, etc.).</p>
            <p><strong>1.4.</strong> &ldquo;Dados do Cliente&rdquo;: todos os dados inseridos, gerados ou
              carregados pelo Cliente e seus Usuários na Plataforma.</p>
            <p><strong>1.5.</strong> &ldquo;LGPD&rdquo;: Lei Geral de Proteção de Dados Pessoais
              (Lei nº 13.709/2018).</p>
          </Section>

          <Section title="2. Aceite e Elegibilidade">
            <p><strong>2.1.</strong> O aceite destes Termos ocorre pela criação de conta ou pelo
              acesso à Plataforma, sendo requisito indispensável para uso.</p>
            <p><strong>2.2.</strong> O Usuário declara ter capacidade civil plena e, quando agir em
              nome do Cliente, ter poderes de representação para vincular a pessoa jurídica.</p>
            <p><strong>2.3.</strong> É vedado o uso da Plataforma por menores de 18 anos sem
              consentimento formal dos responsáveis legais.</p>
          </Section>

          <Section title="3. Descrição do Serviço">
            <p><strong>3.1.</strong> O AutEM oferece, entre outras funcionalidades: gestão de
              serviços em campo, roteirização, rastreamento em tempo real, gestão de frota,
              gestão de pessoas, apuração de remunerações, integrações com provedores de mapas e
              telemetria, auditoria automatizada por Inteligência Artificial e relatórios
              analíticos.</p>
            <p><strong>3.2.</strong> Novas funcionalidades poderão ser incluídas, alteradas ou
              descontinuadas a qualquer tempo, mediante comunicação prévia razoável ao Cliente
              quando impactar funcionalidades já contratadas.</p>
            <p><strong>3.3.</strong> Determinadas integrações de terceiros (mapas, telemetria,
              pagamentos, mensageria) estão sujeitas aos termos próprios desses provedores, aos
              quais o Cliente também adere ao habilitá-las.</p>
          </Section>

          <Section title="4. Conta, Credenciais e Segurança">
            <p><strong>4.1.</strong> O Cliente é responsável por manter em sigilo suas credenciais
              e por todas as ações praticadas com sua conta.</p>
            <p><strong>4.2.</strong> É vedado compartilhar credenciais, criar contas fictícias ou
              tentar acessar áreas restritas da Plataforma sem autorização.</p>
            <p><strong>4.3.</strong> Em caso de uso não autorizado, o Cliente deve notificar
              imediatamente o AutEM pelo canal de suporte oficial.</p>
          </Section>

          <Section title="5. Uso Permitido e Restrições">
            <p><strong>5.1.</strong> O Cliente compromete-se a utilizar a Plataforma somente para
              fins lícitos e em conformidade com a legislação aplicável.</p>
            <p><strong>5.2.</strong> É vedado: (i) engenharia reversa, descompilação ou tentativa
              de extrair código-fonte; (ii) uso para atividades ilícitas, fraudulentas ou que
              violem direitos de terceiros; (iii) sobrecarregar, comprometer ou tentar burlar
              mecanismos de segurança; (iv) revender ou disponibilizar o AutEM a terceiros sem
              autorização; (v) inserir conteúdo ofensivo, discriminatório ou que viole direitos
              autorais.</p>
            <p><strong>5.3.</strong> A violação deste item poderá gerar suspensão imediata da
              conta, sem prejuízo das demais medidas cabíveis.</p>
          </Section>

          <Section title="6. Proteção de Dados (LGPD)">
            <p><strong>6.1.</strong> O AutEM atua como <strong>Operador</strong> dos dados pessoais
              tratados por conta do Cliente (Controlador), nos termos do art. 5º, VII, da LGPD.</p>
            <p><strong>6.2.</strong> Os dados são armazenados em infraestrutura de nuvem com
              criptografia em trânsito (TLS) e em repouso, controle de acesso baseado em papéis,
              logs de auditoria e backups regulares.</p>
            <p><strong>6.3.</strong> O Cliente é responsável por: (i) obter as bases legais
              apropriadas para tratamento dos dados que insere na Plataforma; (ii) atender às
              solicitações de titulares (acesso, correção, exclusão, portabilidade); (iii)
              informar o AutEM sobre exigências legais que exijam a extração ou exclusão de
              dados.</p>
            <p><strong>6.4.</strong> Encarregado (DPO) e canal de contato:{" "}
              <a href="mailto:dpo@satellitus.com" style={{ color: "var(--accent)" }}>
                dpo@satellitus.com
              </a>.</p>
          </Section>

          <Section title="7. Propriedade Intelectual">
            <p><strong>7.1.</strong> Todos os direitos sobre a Plataforma, incluindo marcas,
              logotipos, interfaces, códigos-fonte e documentação, são de titularidade exclusiva
              do AutEM ou de seus licenciantes.</p>
            <p><strong>7.2.</strong> Os Dados do Cliente permanecem de sua propriedade. O Cliente
              concede ao AutEM licença limitada, não exclusiva, para tratar tais dados na medida
              estritamente necessária à prestação do serviço.</p>
          </Section>

          <Section title="8. Disponibilidade e Suporte">
            <p><strong>8.1.</strong> O AutEM envida esforços para manter alta disponibilidade da
              Plataforma, sem, contudo, garantir funcionamento ininterrupto ou isento de falhas.</p>
            <p><strong>8.2.</strong> Manutenções programadas serão comunicadas com antecedência
              razoável, sempre que possível fora do horário comercial.</p>
            <p><strong>8.3.</strong> Suporte técnico disponível via{" "}
              <a href="mailto:suporte@satellitus.com" style={{ color: "var(--accent)" }}>
                suporte@satellitus.com
              </a>{" "}
              em dias úteis, das 9h às 18h (BRT). Planos empresariais poderão contratar SLA
              estendido em termo separado.</p>
          </Section>

          <Section title="9. Limitação de Responsabilidade">
            <p><strong>9.1.</strong> Na máxima extensão permitida pela lei, a responsabilidade do
              AutEM está limitada ao valor efetivamente pago pelo Cliente nos últimos 12 meses de
              contrato.</p>
            <p><strong>9.2.</strong> O AutEM não se responsabiliza por danos indiretos, lucros
              cessantes ou por decisões operacionais tomadas exclusivamente com base nos
              relatórios da Plataforma.</p>
            <p><strong>9.3.</strong> Recomendações da funcionalidade de Auditoria por IA são de
              natureza consultiva e não substituem análise humana qualificada.</p>
          </Section>

          <Section title="10. Pagamento, Vigência e Rescisão">
            <p><strong>10.1.</strong> Condições comerciais (planos, valores, ciclos de faturamento)
              são pactuadas em contrato ou proposta comercial específica.</p>
            <p><strong>10.2.</strong> O inadimplemento superior a 30 dias autoriza a suspensão do
              acesso, sem prejuízo da cobrança.</p>
            <p><strong>10.3.</strong> A rescisão por qualquer das partes deverá ser comunicada com
              antecedência mínima de 30 dias. Após a rescisão, os Dados do Cliente permanecem
              disponíveis para exportação por 30 dias.</p>
          </Section>

          <Section title="11. Alterações destes Termos">
            <p><strong>11.1.</strong> O AutEM poderá revisar estes Termos a qualquer tempo,
              publicando a versão atualizada nesta página e notificando os Usuários pelos canais
              oficiais.</p>
            <p><strong>11.2.</strong> O uso continuado após a publicação da nova versão constitui
              aceite às alterações.</p>
          </Section>

          <Section title="12. Lei Aplicável e Foro">
            <p><strong>12.1.</strong> Estes Termos são regidos pelas leis da República Federativa
              do Brasil.</p>
            <p><strong>12.2.</strong> Fica eleito o foro da Comarca da sede do AutEM para dirimir
              quaisquer controvérsias, com renúncia expressa a qualquer outro, por mais
              privilegiado que seja.</p>
          </Section>

          <Section title="13. Contato">
            <p>Dúvidas, sugestões ou solicitações relacionadas a estes Termos podem ser
              encaminhadas para:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Suporte: <a href="mailto:suporte@satellitus.com" style={{ color: "var(--accent)" }}>suporte@satellitus.com</a> (Seg-Sex, 9h às 18h)</li>
              <li>Encarregado LGPD (DPO): <a href="mailto:dpo@satellitus.com" style={{ color: "var(--accent)" }}>dpo@satellitus.com</a></li>
              <li>Institucional: <a href="mailto:contato@satellitus.com" style={{ color: "var(--accent)" }}>contato@satellitus.com</a></li>
            </ul>
          </Section>

          <div className="pt-4 mt-8 border-t border-gray-200 text-xs text-gray-400 text-center">
            © {new Date().getFullYear()} AutEM Tecnologia · Todos os direitos reservados
          </div>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-bold text-gray-900 mb-3">{title}</h2>
      <div className="space-y-2 text-[14.5px]">{children}</div>
    </section>
  );
}
